import React, { useState, useRef } from 'react';
import { Camera, X, Upload, User } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoUploadSection = ({ employeeId, currentPhotoUrl, onPhotoChange, isNew = false }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPEG or PNG image',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (10MB max before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 10MB',
        variant: 'destructive'
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // If editing existing employee, upload immediately
    if (employeeId && !isNew) {
      uploadPhoto(file);
    } else {
      // For new employees, store file for later upload
      setPendingFile(file);
      if (onPhotoChange) {
        onPhotoChange(file);
      }
    }
  };

  const uploadPhoto = async (file) => {
    if (!employeeId) {
      toast({
        title: 'Error',
        description: 'Please save the employee first before uploading a photo',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/api/photos/upload/${employeeId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: 'Success',
        description: 'Photo uploaded successfully'
      });

      if (onPhotoChange) {
        onPhotoChange(response.data.photo_url);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload photo',
        variant: 'destructive'
      });
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (isNew || !employeeId) {
      setPreviewUrl(null);
      setPendingFile(null);
      if (onPhotoChange) {
        onPhotoChange(null);
      }
      return;
    }

    try {
      await api.delete(`/api/photos/${employeeId}`);
      setPreviewUrl(null);
      toast({
        title: 'Success',
        description: 'Photo removed successfully'
      });
      if (onPhotoChange) {
        onPhotoChange(null);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove photo',
        variant: 'destructive'
      });
    }
  };

  const displayUrl = previewUrl || (currentPhotoUrl ? `${API_URL}${currentPhotoUrl}` : null);

  return (
    <div className="flex flex-col items-center space-y-3" data-testid="photo-upload-section">
      <div className="relative">
        {/* Photo circle */}
        <div 
          className="w-28 h-28 rounded-full border-4 border-gray-200 overflow-hidden bg-gray-100 flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          data-testid="photo-circle"
        >
          {displayUrl ? (
            <img 
              src={displayUrl} 
              alt="Employee" 
              className="w-full h-full object-cover"
              data-testid="employee-photo"
            />
          ) : (
            <User className="w-12 h-12 text-gray-400" />
          )}
          
          {/* Upload overlay */}
          <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center transition-all opacity-0 hover:opacity-100">
            <Camera className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Remove button */}
        {displayUrl && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemovePhoto();
            }}
            className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
            data-testid="remove-photo-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Loading overlay */}
        {uploading && (
          <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="photo-input"
      />

      {/* Upload button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="text-xs"
        data-testid="upload-photo-btn"
      >
        <Upload className="w-3 h-3 mr-1" />
        {uploading ? 'Uploading...' : (displayUrl ? 'Change Photo' : 'Upload Photo')}
      </Button>

      <p className="text-xs text-gray-500 text-center">
        JPEG or PNG, max 2MB<br />
        Auto-compressed
      </p>
    </div>
  );
};

export default PhotoUploadSection;
